/**
 * ACTIVITY-CORE (L1) — immediate session undo.
 *
 * In-memory stack (emptied on reload or account switch) + toast with
 * "Undo" action + Ctrl/Cmd+Z. Each entry applies the INVERSE mutation through
 * PowerSync's normal write-path (never rewinds state); durable/collaborative
 * undo is the activity-feed revert (server-side).
 */
import { msg } from "@lingui/core/macro";
import { createUndoStack } from "@agape/shared";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";

/** Single app-wide stack (scope: tab + active account). */
export const appUndoStack = createUndoStack();

interface UseUndoResult {
  /**
   * Registers an undoable action and shows the toast with "Undo".
   * `message` arrives already localized (the caller knows the context).
   */
  registerUndo: (message: string, undo: () => Promise<void> | void) => void;
  /** Undoes the last action (Ctrl+Z). true if there was something to undo. */
  undoLast: () => Promise<boolean>;
  /** Empties the stack (account/session switch). */
  clearUndoStack: () => void;
}

export function useUndo(): UseUndoResult {
  const { t } = useI18n();

  const registerUndo = (message: string, undo: () => Promise<void> | void) => {
    const remove = appUndoStack.push({ label: message, undo });
    showToast(message, "info", {
      action: {
        label: t(msg`Undo`),
        onClick: () => {
          // Consume THIS action (even if it is no longer the last) without duplicating Ctrl+Z.
          if (remove()) {
            void Promise.resolve(undo()).catch((err: unknown) => {
              console.error("[undo] toast undo failed:", err);
              showToast(t(msg`Could not undo the action`), "error");
            });
          }
        },
      },
    });
  };

  const undoLast = async () => {
    try {
      const undone = await appUndoStack.undoLast();
      if (!undone) {
        return false;
      }
      showToast(t(msg`Action undone`), "success");
      return true;
    } catch (err: unknown) {
      console.error("[undo] Ctrl+Z undo failed:", err);
      showToast(t(msg`Could not undo the action`), "error");
      return true;
    }
  };

  return { registerUndo, undoLast, clearUndoStack: () => appUndoStack.clear() };
}
